import {
  FocusEvent,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import styled from "styled-components";
import { useTranslation } from "next-i18next";
import { getCookie } from "cookies-next";
import { useRouter } from "next/router";
import { useCopyToClipboard, useInterval } from "react-use";
import { Button, Dropdown, Input, Select, Slider } from "antd";
import classNames from "classnames";
import { clamp } from "lodash";

import { poppins } from "@/components/common/fonts";
import { MyProjectItemDetail } from "@/types/dashboard.api";
import {
  IconClose,
  IconCode,
  IconCopy,
  IconDiamond16,
} from "@/components/common/Icons";
import { useNotification } from "@/hooks/useNotification";
import {
  DEFAULT_SCORM_COMPLETION_CRITERIA,
  DEFAULT_SCORM_VERSION,
  MAX_SCORM_COMPLETION_CRITERIA,
  MIN_SCORM_COMPLETION_CRITERIA,
  SCORM_VERSION,
  ScormVersion,
} from "@/constants/scorm";
import { ProjectForPublishScorm, PublishedScorm } from "@/types/scorm";
import { ValueOf } from "@/types/type";

const PUBLISH_TYPE = {
  PUBLISH: "PUBLISH",
  REPUBLISH: "REPUBLISH",
  UNPUBLISH: "UNPUBLISH",
} as const;
type PublishType = ValueOf<typeof PUBLISH_TYPE>;

export const PublishVideoDropdown = ({
  project,
  showCopyCdnLink,
  canExportScormPackage,

  isMutatingScorm,
  onPublishAndDownload,
  onUnpublish,
  onRepublish,

  children,

  publishedScorm,
  versions,
}: {
  project?: MyProjectItemDetail;
  showCopyCdnLink?: boolean;
  canExportScormPackage?: boolean;

  publishedScorm?: PublishedScorm;
  versions?: ProjectForPublishScorm[];

  isMutatingScorm?: boolean;
  onPublishAndDownload?: (params: {
    scormVersion: ScormVersion;
    completionCriteria: number;
  }) => Promise<void>;
  onUnpublish?: () => void;
  onRepublish?: () => Promise<void>;

  children?: ReactNode;
}) => {
  const [isOpened, setIsOpened] = useState(false);

  return (
    <StyledDropdown
      open={isOpened}
      trigger={["click"]}
      onOpenChange={(open) => setIsOpened(open)}
      dropdownRender={() => (
        <DropdownContent
          project={project}
          showCopyCdnLink={showCopyCdnLink}
          canExportScormPackage={canExportScormPackage}
          publishedScorm={publishedScorm}
          versions={versions}
          isMutatingScorm={isMutatingScorm}
          onPublishAndDownload={onPublishAndDownload}
          onUnpublish={onUnpublish}
          onRepublish={onRepublish}
          onClose={() => setIsOpened(false)}
        />
      )}
      destroyPopupOnHide
      placement="bottomRight"
      align={{
        offset: [0, 13],
      }}
    >
      {children}
    </StyledDropdown>
  );
};

const StyledDropdown = styled(Dropdown)``;

const DropdownContent = ({
  project,
  showCopyCdnLink,
  canExportScormPackage,

  publishedScorm,
  versions,

  isMutatingScorm,
  onPublishAndDownload,
  onUnpublish,
  onRepublish,

  onClose,
}: {
  project?: MyProjectItemDetail;
  showCopyCdnLink?: boolean;
  canExportScormPackage?: boolean;

  publishedScorm?: PublishedScorm;
  versions?: ProjectForPublishScorm[];

  isMutatingScorm?: boolean;
  onPublishAndDownload?: (params: {
    scormVersion: ScormVersion;
    completionCriteria: number;
  }) => Promise<void>;
  onUnpublish?: () => void;
  onRepublish?: () => Promise<void>;

  onClose?: () => void;
}) => {
  const locale = (getCookie("NEXT_LOCALE") || "en") as string;

  const router = useRouter();

  const { t } = useTranslation("common");

  const [, copyToClipboard] = useCopyToClipboard();

  const { contextHolder, notify } = useNotification();

  const [scormVersion, setScormVersion] = useState<ScormVersion>(
    DEFAULT_SCORM_VERSION,
  );

  const [completionCriteria, setCompletionCriteria] = useState<number>(
    DEFAULT_SCORM_COMPLETION_CRITERIA,
  );
  const [inputtingCompletionCriteria, setInputtingCompletionCriteria] =
    useState(() => `${completionCriteria}`);

  useEffect(
    () => setInputtingCompletionCriteria(`${completionCriteria}`),
    [completionCriteria],
  );

  /**
   * 입력 완료 후 처리
   * - 입력 범위 제한
   * - 스텝 제한
   */
  const completeCompletionCriteriaInput = useCallback(
    (e: FocusEvent<HTMLInputElement> | KeyboardEvent<HTMLInputElement>) => {
      const parsed = parseInt(inputtingCompletionCriteria, 10) || 0;
      const stepped = Math.round(parsed / 10) * 10;
      const clamped = clamp(
        stepped,
        MIN_SCORM_COMPLETION_CRITERIA,
        MAX_SCORM_COMPLETION_CRITERIA,
      );
      const stringified = `${clamped}`;

      setCompletionCriteria(clamped);
      setInputtingCompletionCriteria(stringified);

      e.currentTarget.value = stringified;
    },
    [inputtingCompletionCriteria],
  );

  /**
   * 부정확해도 됨
   *
   * - 10초마다 현재 시간 갱신
   */
  const [now, setNow] = useState(() => Date.now());

  useInterval(() => {
    setNow(Date.now());
  }, 1000 * 10);

  const humanReadableCreatedAt = useMemo(() => {
    if (!publishedScorm?.project?.createdAt) {
      return "";
    }

    const date = new Date(publishedScorm.project.createdAt);
    const diff = now - date.getTime();
    const diffSeconds = Math.floor(diff / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return date.toLocaleDateString(locale, {
        month: "short",
        day: "numeric",
        hour12: true,
        hour: "numeric",
        minute: "numeric",
      });
    } else if (diffHours > 0) {
      return t(`{{hours}} hours ago`, { hours: diffHours });
    } else {
      return t(`{{minutes}} minutes ago`, { minutes: diffMinutes });
    }
  }, [publishedScorm?.project?.createdAt, locale, now, t]);

  /**
   * 퍼블리시 타입
   * - 퍼블리시, 리퍼블리시, 언퍼블리시
   */
  const publishType = useMemo<PublishType>(() => {
    if (!canExportScormPackage) {
      return PUBLISH_TYPE.PUBLISH;
    }

    if (!publishedScorm) {
      return PUBLISH_TYPE.PUBLISH;
    }

    if (publishedScorm.projectId === project?._id) {
      return PUBLISH_TYPE.UNPUBLISH;
    } else {
      return PUBLISH_TYPE.REPUBLISH;
    }
  }, [canExportScormPackage, publishedScorm, project]);

  /**
   * 퍼블리시 타입이 변경되면 스테이트 초기화
   */
  useEffect(() => {
    setScormVersion(DEFAULT_SCORM_VERSION);
    setCompletionCriteria(DEFAULT_SCORM_COMPLETION_CRITERIA);
  }, [publishType]);

  /**
   * 공유 또는 퍼블리시 기능 동작
   */
  const handleCopyLink = useCallback(() => {
    const url = `${window.location.origin}${router.asPath}`;

    copyToClipboard(url);

    notify.success(
      t(
        "The shared link has been copied to your clipboard. Anyone with the link can access it.",
      ),
    );
  }, [router, notify, copyToClipboard, t]);

  const handleCopyCdnLink = useCallback(() => {
    const cdnUrl = project?.download_url;

    if (!cdnUrl) {
      return;
    }

    copyToClipboard(cdnUrl);

    notify.success(
      t(
        "The CDN URL has been copied to clipboard. Anyone with the link can access it.",
      ),
    );
  }, [project?.download_url, copyToClipboard, notify, t]);

  const handleCopyEmbedCode = useCallback(() => {
    if (!project?.download_url || !project?.name) {
      return;
    }

    const code = `<div style="position: relative; overflow: hidden; aspect-ratio: 1920/1080"><iframe src="${project?.download_url}" loading="lazy" title="DeepbrainAiStudios-${project?.name}" allow="encrypted-media; fullscreen;" style="position: absolute; width: 100%; height: 100%; top: 0; left: 0; border: none; padding: 0; margin: 0; overflow:hidden;"></iframe></div>`;

    copyToClipboard(code);

    notify.success(
      t(
        "The embed code has been copied to clipboard. Anyone with link can access it.",
      ),
    );
  }, [project, copyToClipboard, notify, t]);

  const handlePublishAndDownload = useCallback(async () => {
    if (isMutatingScorm) {
      return;
    }

    /**
     * 권한이 없을 때는 닫고 이어서 다음 코드 진행
     */
    if (!canExportScormPackage) {
      onClose?.();
    }

    await onPublishAndDownload?.({
      scormVersion,
      completionCriteria,
    });
  }, [
    isMutatingScorm,
    canExportScormPackage,
    onClose,
    onPublishAndDownload,
    scormVersion,
    completionCriteria,
  ]);

  const handleUnpublish = useCallback(async () => {
    if (isMutatingScorm) {
      return;
    }

    if (!canExportScormPackage) {
      onClose?.();
      return;
    }

    await onUnpublish?.();
  }, [isMutatingScorm, canExportScormPackage, onClose, onUnpublish]);

  const handleRepublish = useCallback(async () => {
    if (isMutatingScorm) {
      return;
    }

    if (!canExportScormPackage) {
      onClose?.();
      return;
    }

    await onRepublish?.();
  }, [isMutatingScorm, canExportScormPackage, onClose, onRepublish]);

  return (
    <ContentContainer>
      <IconClose
        css={`
          position: absolute;
          right: 20px;
          top: 25px;
          width: 10px;
          height: 10px;
          cursor: pointer;

          transition: opacity 0.2s;

          &:hover {
            opacity: 0.5;
          }
        `}
        viewBox="0 0 18 18"
        onClick={onClose}
      />

      <Title>
        {canExportScormPackage && !!publishedScorm
          ? t("Published video")
          : t("Publish video")}
      </Title>
      <Description>
        {canExportScormPackage && !!publishedScorm
          ? t(
              "The published video is now available on its dedicated video page.",
            )
          : t(
              "Create a shareable link or embed code for seamless external video sharing.",
            )}
      </Description>

      {/* 
        퍼블리시 가능일 때
      */}
      {publishType === PUBLISH_TYPE.PUBLISH && (
        <PublishArea>
          <PublishOptions
            className={classNames({
              disabled: !canExportScormPackage,
              loading: isMutatingScorm,
            })}
          >
            <PublishOptionsTitle>{t("SCORM")}</PublishOptionsTitle>
            <PublishOption>
              <PublishOptionTitle>{t("Export version")}</PublishOptionTitle>
              <PublishOptionInputs>
                <ScormVersionSelect
                  options={[
                    {
                      label: "SCORM 1.2",
                      value: SCORM_VERSION.SCORM_1_2,
                    },
                    {
                      label: "SCORM 2004",
                      value: SCORM_VERSION.SCORM_2004,
                    },
                  ]}
                  value={scormVersion}
                  onChange={(value) => setScormVersion(value)}
                  /**
                   * 드롭다운 내부의 셀렉트의 옵션을 클릭하면\
                   * 드롭다운 외부를 클릭한 것으로 판단되어 드롭다운이 닫힘\
                   * 이것을 회피하기 위해\
                   * 셀렉트의 옵션을 클릭하면 preventDefault를 호출하여\
                   * 드롭다운이 닫히지 않도록 함
                   */
                  optionRender={(option) => (
                    <div onPointerDown={(e) => e.preventDefault()}>
                      {option.label}
                    </div>
                  )}
                />
              </PublishOptionInputs>
            </PublishOption>

            <PublishOption>
              <PublishOptionTitle>
                {t("Completion criteria")}
              </PublishOptionTitle>

              <PublishOptionInputs>
                <CompletionCriteriaSlider
                  min={MIN_SCORM_COMPLETION_CRITERIA}
                  max={MAX_SCORM_COMPLETION_CRITERIA}
                  step={10}
                  tooltip={{
                    open: false,
                  }}
                  value={completionCriteria}
                  onChange={(value) => setCompletionCriteria(value)}
                />

                <CompletionCriteriaInput
                  suffix="%"
                  value={inputtingCompletionCriteria}
                  onChange={(e) =>
                    setInputtingCompletionCriteria(e.target.value)
                  }
                  onBlur={(e) => {
                    completeCompletionCriteriaInput(e);
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter") {
                      return;
                    }

                    completeCompletionCriteriaInput(e);
                  }}
                />
              </PublishOptionInputs>
            </PublishOption>
          </PublishOptions>

          <PublishButton
            type="primary"
            loading={isMutatingScorm}
            onClick={handlePublishAndDownload}
          >
            {t("Export SCORM Package")}
            {!canExportScormPackage && (
              <IconDiamond16
                css={`
                  &&& path {
                    fill: currentColor;
                    stroke: transparent;
                  }
                `}
              />
            )}
          </PublishButton>
        </PublishArea>
      )}

      {/* 
        퍼블리시 된 경우 퍼블리시 된 버전의 썸네일 등을 표시함
      */}
      {([PUBLISH_TYPE.UNPUBLISH, PUBLISH_TYPE.REPUBLISH] as string[]).includes(
        publishType,
      ) && (
        <ProjectInformation>
          <ProjectThumbnail>
            <img
              src={publishedScorm?.project.thumbnailUrl}
              alt={publishedScorm?.project.name}
            />
          </ProjectThumbnail>
          <ProjectTitle>
            {t("Version {{version}}", {
              version: publishedScorm?.project.version,
            })}
          </ProjectTitle>
          <ProjectDescription>{humanReadableCreatedAt}</ProjectDescription>
        </ProjectInformation>
      )}

      {/*
       * 언퍼블리시일 때
       */}
      {publishType === PUBLISH_TYPE.UNPUBLISH && (
        <UnRepublishArea>
          <UnRepublishDescription>
            {t(
              "This version of the video was previously exported as a SCORM package.",
            )}
          </UnRepublishDescription>
          <UnRepublishButton
            loading={isMutatingScorm}
            onClick={handleUnpublish}
          >
            {t("Unpublish")}
          </UnRepublishButton>
        </UnRepublishArea>
      )}

      {/*
       * 리퍼블리시일 때
       */}
      {publishType === PUBLISH_TYPE.REPUBLISH && (
        <UnRepublishArea>
          <UnRepublishDescription>
            {t(
              "Publish Version {{replaceVersion}} to replace Version {{currentVersion}}",
              {
                replaceVersion: versions?.find(
                  (version) => version._id === project?._id,
                )?.version,
                currentVersion: publishedScorm?.project.version,
              },
            )}
          </UnRepublishDescription>
          <UnRepublishButton
            loading={isMutatingScorm}
            onClick={handleRepublish}
          >
            {t("Republish")}
          </UnRepublishButton>
        </UnRepublishArea>
      )}

      <HorizontalBar />

      <Title>{t("Share")}</Title>

      <ShareButtons>
        {!!project?.download_url && !!project?.name && (
          <ShareButton
            color="#696969"
            icon={<IconCode viewBox="0 0 16 17" />}
            text={t("Embed")}
            onClick={handleCopyEmbedCode}
          />
        )}

        <ShareButton
          color="#5BD4E5"
          icon={<IconCopy viewBox="0 0 12 12" />}
          text={t("Video link")}
          onClick={handleCopyLink}
        />

        {showCopyCdnLink && !!project?.download_url && (
          <ShareButton
            color="#B26CC8"
            icon={<IconCopy viewBox="0 0 12 12" />}
            text={t("CDN link")}
            onClick={handleCopyCdnLink}
          />
        )}
      </ShareButtons>

      {contextHolder}
    </ContentContainer>
  );
};

const ContentContainer = styled.div`
  padding: 25px 20px;
  flex-direction: column;
  align-items: flex-start;

  width: 380px;
  max-height: calc(100vh - 70px);
  overflow: auto;
  box-sizing: border-box;

  border-radius: 10px;
  background: #fff;
  box-shadow: 0px 4px 30px 0px rgba(0, 0, 0, 0.25);
`;

const Title = styled.div`
  margin-bottom: 10px;

  color: #222;
  font-family: ${poppins.string};
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
`;

const Description = styled.div`
  margin-bottom: 20px;

  color: #000;
  font-family: ${poppins.string};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
`;

const PublishArea = styled.div`
  margin-bottom: 30px;
`;

/**
 * - &.disabled
 * - &.loading
 */
const PublishOptions = styled.div`
  display: flex;
  padding: 10px;
  flex-direction: column;
  justify-content: center;
  gap: 14px;

  border-radius: 6px;
  border: 1px solid #d9d9d9;

  margin-bottom: 10px;

  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  &.loading {
    opacity: 0.7;
    pointer-events: none;
  }
`;

const PublishOptionsTitle = styled.div`
  color: #000;
  font-family: ${poppins.string};
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px; /* 133.333% */
`;

const PublishOption = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;

  margin-left: 22px;
`;

const PublishOptionTitle = styled.div`
  flex: 1 0 0;

  color: #000;
  font-family: ${poppins.string};
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px; /* 133.333% */

  white-space: nowrap;
`;

const PublishOptionInputs = styled.div`
  flex: 2;

  display: flex;
  align-items: center;
  justify-content: end;
  gap: 12px;
`;

const ScormVersionSelect = styled(Select<ScormVersion>)`
  width: 128px;
  height: 30px;

  color: #222;
  font-family: ${poppins.string};
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`;

const CompletionCriteriaSlider = styled(Slider)`
  flex: 1;
`;

const CompletionCriteriaInput = styled(Input)`
  width: 62px;
  height: 30px;

  color: #222;
  text-align: center;
  font-family: ${poppins.string};
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`;

const PublishButton = styled(Button)`
  width: 342px;
  height: 44px;

  border-radius: 6px;
  background: #4276ee;
  box-shadow: 0px 3px 6px 0px rgba(0, 124, 137, 0.2);

  color: #fff;
  font-family: ${poppins.string};
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px; /* 142.857% */
`;

const UnRepublishArea = styled.div`
  display: flex;
  gap: 40px;
  align-items: center;
  justify-content: space-between;

  padding: 0 10px;
  margin-bottom: 30px;
`;

const UnRepublishDescription = styled.div`
  width: 154px;

  color: #000;
  font-family: ${poppins.string};
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px; /* 133.333% */
`;

const UnRepublishButton = styled(Button)`
  width: 120px;
  height: 40px;

  border-radius: 7px;
  border: 1px solid #9e9e9e;

  color: #9e9e9e;
  text-align: center;
  font-family: ${poppins.string};
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  text-transform: capitalize;
`;

const HorizontalBar = styled.div`
  width: 100%;
  height: 1px;
  background: #4444444d;
  margin-bottom: 20px;
`;

const ShareButtons = styled.div`
  display: flex;
  gap: 40px;
  justify-content: center;
`;

const ShareButton = ({
  color,
  icon,
  text,
  onClick,
}: {
  color: string;
  icon: ReactNode;
  text: string;
  onClick: () => void;
}) => {
  return (
    <ShareButtonContainer onClick={onClick} type="text">
      <ShareButtonIcon $backgroundColor={color}>{icon}</ShareButtonIcon>
      <ShareButtonText>{text}</ShareButtonText>
    </ShareButtonContainer>
  );
};

const ShareButtonContainer = styled(Button)`
  display: flex;
  flex-direction: column;
  gap: 6px;

  height: auto;
  padding: 0;
`;

const ShareButtonIcon = styled.div<{ $backgroundColor: string }>`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 60px;
  height: 60px;
  border-radius: 9999px;
  background: ${({ $backgroundColor }) => $backgroundColor};

  svg {
    width: 24px;
    height: 24px;
  }
  svg path {
    stroke: #fff;
  }
`;

const ShareButtonText = styled.div`
  color: #000;
  font-family: ${poppins.string};
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px; /* 133.333% */
`;

const ProjectInformation = styled.div`
  margin-bottom: 18px;
`;

const ProjectThumbnail = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  margin: 0 8px;
  border-radius: 6px;
  border: 1px solid #ededed;
  overflow: hidden;
  aspect-ratio: 16 / 9;
  margin-bottom: 4px;

  & img {
    pointer-events: none;
    width: 100%;
    height: auto;
  }
`;

const ProjectTitle = styled.div`
  text-align: center;
  color: #000;
  font-family: ${poppins.string};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ProjectDescription = styled.div`
  text-align: center;
  color: #777;
  font-family: ${poppins.string};
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;
